import { NAV_PERMISSIONS, visibleNavIds } from '../constants/nav';

/**
 * Which destinations reach the phone's bottom bar.
 *
 * The bar holds FOUR items. Not a style preference: at 360px — the narrowest
 * device the clinic actually sees — a fifth item takes each target below the
 * 44px floor the rest of this design keeps to, and a tab bar whose labels are
 * unreadable and whose targets are unhittable is worse than a menu.
 *
 * So when a person can see more than four sections, the bar shows the first
 * three and folds the rest into «بیشتر». The order is `NAV_PERMISSIONS`'s own,
 * which is the sidebar's order — the two surfaces must not disagree about
 * which section matters most, and reading the same array is the only way to
 * guarantee that as sections are added.
 *
 * Everything here is derived from the SERVER'S resolved per-clinic permissions
 * (`useAccess`), never from the token: the flat `perm.*` JWT claims carry no
 * clinic, so trusting them would show clinic A's destinations while standing
 * in clinic B.
 *
 * See docs/MOBILE-REDESIGN.md §3.2.
 */

/** The id the fold uses. Not a route — it opens the more-sheet. */
export const MORE_ID = 'more';

/** How many real destinations fit before the fold. */
export const MAX_ITEMS = 4;

/**
 * @param access  the object from `useAccess()`
 * @returns {{ items: string[], overflow: string[], folded: boolean }}
 *          `items` are the bar's ids in order (possibly ending in MORE_ID);
 *          `overflow` are the destinations the more-sheet must list.
 */
export function bottomNavPlan(access) {
  // `visibleNavIds` is the sidebar's own resolver, including its `ready`
  // guard. Reading it rather than re-filtering `NAV_PERMISSIONS` is what makes
  // "the bar and the sidebar can never disagree" true by construction instead
  // of by review.
  const visible = visibleNavIds(access);

  // A person with nothing gets no bar at all — never a row of disabled icons.
  // ClinicGate already sends them to the no-permission page; a bar under it
  // would be four controls that each answer "not you".
  if (visible.length === 0) return { items: [], overflow: [], folded: false };

  if (visible.length <= MAX_ITEMS) {
    return { items: visible, overflow: [], folded: false };
  }

  return {
    items: [...visible.slice(0, MAX_ITEMS - 1), MORE_ID],
    overflow: visible.slice(MAX_ITEMS - 1),
    folded: true,
  };
}

/**
 * Which bar item is active for a bare (clinic-stripped) pathname.
 *
 * Derived on every render rather than stored, so a deep link, a redirect and a
 * browser-back all light the same tab with no extra bookkeeping. A path whose
 * section is behind the fold reports MORE_ID, so the bar still shows where the
 * operator is.
 */
export function activeNavId(barePath, plan) {
  const path = barePath || '/';
  const match = NAV_PERMISSIONS
    .map((e) => e.id)
    .filter((id) => path === `/${id}` || path.startsWith(`/${id}/`))
    .sort((a, b) => b.length - a.length)[0];

  if (!match) return null;
  if (plan.items.includes(match)) return match;
  if (plan.overflow.includes(match)) return MORE_ID;
  return null;
}
